<template>
  <div class="task-stage-page">

    <!-- Full analysis: run on demand; the text can be used as the conclusion -->
    <section v-if="getClient.aiModule" class="v2-field task-analysis" data-qid="task-full-analysis">
      <header class="v2-field__head">
        <h4><font-awesome-icon icon="sparkles" class="mr-2"/>{{ $t('tasks.modal.full_analyst') }}</h4>
        <span v-if="isFullAnalystDrawerOpen && !ailoading"
              class="v2-ai-btn"
              @click="runAgain"
              data-qid="task-full-analysis-again">
          <font-awesome-icon icon="sparkles"/>{{ $t('tasks.v2.run_again') }}
        </span>
        <span v-if="isFullAnalystDrawerOpen" class="v2-field__icon" @click="toggleFullAnalystDrawer()"><font-awesome-icon icon="times"/></span>
      </header>
      <template v-if="!isFullAnalystDrawerOpen">
        <p class="task-analysis__hint">{{ $t('tasks.v2.full_analysis_hint') }}</p>
        <b-button size="sm"
                  variant="secondary"
                  @click="toggleFullAnalystDrawer()"
                  data-qid="task-full-analysis-run">
          <font-awesome-icon icon="sparkles" class="mr-1"/>{{ $t('tasks.v2.run_full_analysis') }}
        </b-button>
      </template>
      <div v-else-if="ailoading" class="task-ai__loading">
        <font-awesome-icon icon="spinner" class="fa-spin mr-2"/>{{ $t('tasks.modal.please_wait_while_processing') }} ({{ timeLeft }}s)
      </div>
      <template v-else>
        <b-alert v-if="errorFullAnalystDrawer" show variant="danger">{{ errorFullAnalystDrawer }}</b-alert>
        <div v-for="(choice, index) in fullAnalystAi" :key="'analysis-' + index" class="task-analysis__result">
          <div class="text-formatted">{{ choice.message.content }}</div>
          <div class="task-ai__buttons" v-if="canEdit">
            <b-button size="sm"
                      variant="secondary"
                      @click="useAsConclusion(choice.message.content)"
                      data-qid="task-full-analysis-use">
              <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('tasks.v2.use_as_conclusion') }}
            </b-button>
          </div>
        </div>
      </template>
    </section>

    <!-- Conclusion: the response (saved when clicked) and the conclusion text -->
    <EngagementTaskTextCard v-if="hasDescription"
                            field="remarks"
                            :title="$t('tasks.modal.remarks_label')"
                            :required="selectedTask.add_description === 'Mandatory'"
                            :placeholder="$t('tasks.modal.remarks_placeholder')"
                            report
    >
      <EngagementTaskResponse v-if="hasResponse"/>
    </EngagementTaskTextCard>
    <section v-else-if="hasResponse" class="v2-field">
      <header class="v2-field__head"><h4>{{ $t('tasks.modal.remarks_label') }}</h4></header>
      <EngagementTaskResponse/>
    </section>

    <!-- Observations (can be switched off in the engagement settings) -->
    <section v-if="observationsOn" class="v2-field" data-qid="task-observations">
      <header class="v2-field__head">
        <h4>{{ $t('projects.observations.title') }}</h4>
        <CreateObservationModal v-if="canEdit"
                                :object="selectedTask.project"
                                :task="selectedTask"
                                type="global"
                                @updated="loadObservations(selectedTask.id)"
        ></CreateObservationModal>
      </header>
      <div v-for="observation in sTableItems" :key="'observation-' + observation.id" class="task-observation">
        <font-awesome-icon icon="binoculars" class="task-observation__icon"/>
        <a href="#" class="task-observation__name" @click.prevent="openViewModal(observation)">{{ observation.name }}</a>
        <template v-if="canEdit">
          <span class="v2-field__icon" @click="showEditModal(observation)"><font-awesome-icon icon="pen"/></span>
          <span class="v2-field__icon v2-field__icon--danger" @click="showSConfirmDeleteModal(observation)"><font-awesome-icon icon="trash-alt"/></span>
        </template>
      </div>
      <div v-if="!loadingObservations && !sTableItems.length" class="v2-field__empty">{{ $t('tasks.v2.no_observations') }}</div>
      <ObservationViewModal :object="selectedObservation"
                            :project="selectedTask.project"
                            :showModal="viewObservationModal"
                            @closeViewModal="handleCloseViewModal"
      ></ObservationViewModal>
    </section>

    <!-- Finalize: only once D&I, the tests and the conclusion are done -->
    <div v-if="canEdit"
         class="task-next"
         :class="{'task-next--ready': !finalizeBlockedReason}"
         data-qid="task-finalize">
      <font-awesome-icon :icon="finalizeBlockedReason ? 'info-circle' : 'check'" class="mr-1"/>
      <span class="task-next__text">
        <b v-if="!finalizeBlockedReason">{{ $t('tasks.v2.ready_to_finalize') }}</b>
        {{ finalizeBlockedReason || $t('tasks.v2.finalize_hint') }}
      </span>
      <b-button variant="success"
                :disabled="!!finalizeBlockedReason || buttonDisabled"
                @click="selectedTask.qa_status === 'rejected' ? showReopenQaTaskComplete() : finalizeTask()"
                data-qid="task-finalize-button"
      >{{ $t('tasks.v2.finalize') }}</b-button>
    </div>
  </div>
</template>

<script>
import assessmentSingleTaskProxy from './assessmentSingleTaskProxy';
import CreateObservationModal from '@/views/Projects/CreateObservationModal.vue';
import ObservationViewModal from '@/views/Projects/ObservationViewModal.vue';
import EngagementTaskTextCard from './EngagementTaskTextCard.vue';
import EngagementTaskResponse from './EngagementTaskResponse.vue';
import { observationsEnabled } from '@/views/Projects/Engagement/engagement.js';

// Conclusion page of the task panel
export default {
  name: 'EngagementTaskConclusion',
  mixins: [assessmentSingleTaskProxy],
  components: {
    CreateObservationModal,
    ObservationViewModal,
    EngagementTaskTextCard,
    EngagementTaskResponse
  },
  computed: {
    canEdit() {
      return this.selectedTask && !this.readOnly;
    },
    hasResponse() {
      return this.selectedTask.add_response === 'Optional' || this.selectedTask.add_response === 'Mandatory';
    },
    hasDescription() {
      return this.selectedTask.add_description === 'Optional' || this.selectedTask.add_description === 'Mandatory';
    },
    observationsOn() {
      return observationsEnabled(this.selectedTask && this.selectedTask.project);
    }
  },
  methods: {
    useAsConclusion(text) {
      this.taskForm.remarks = this.taskForm.remarks ? this.taskForm.remarks + '\n\n' + text : text;
      this.editModeConclusion = true;
    },
    runAgain() {
      this.toggleFullAnalystDrawer();
      this.$nextTick(() => this.toggleFullAnalystDrawer());
    }
  }
};
</script>
